// Indonesian numbers reach the forms in three shapes — 081211112222 from a
// phone's contact list, 6281211112222 from an export, +62 812-1111-2222 typed
// by hand — and every one of them has to end up as the same +62 number. The
// mask alone could not do that: it dropped the digits into its slots verbatim,
// so a leading 0 became +62081211112222.

// The longest Indonesian national significant number, and the width the old
// "+62############" mask allowed — so nothing that used to fit is now rejected.
const MAX_DIGITS = 12;

// The country code is only stripped when what follows is long enough to be a
// real number. Without that guard the "62" of someone typing their own country
// code digit by digit would be eaten before they finished.
const MIN_DIGITS_AFTER_COUNTRY_CODE = 8;

const stripLeadingZeros = (digits) => digits.replace(/^0+/, "");

// Returns the national significant number: digits only, no country code, no
// trunk prefix. "" when there is nothing usable.
export const normalizeIndonesianPhoneDigits = (input) => {
  const raw = String(input ?? "").trim();

  // A written-out "+62" says country code outright, so it comes off whatever
  // follows — including nothing, which is how a cleared field reaches us. Only
  // a bare 62 has to be guessed at, below.
  const withoutCallingCode = raw.startsWith("+62")
    ? raw.slice(3)
    : raw.startsWith("+")
      ? raw.slice(1)
      : raw;

  let digits = withoutCallingCode.replace(/[^0-9]/g, "");

  digits = stripLeadingZeros(digits);

  if (
    digits.startsWith("62") &&
    digits.length - 2 >= MIN_DIGITS_AFTER_COUNTRY_CODE
  ) {
    digits = stripLeadingZeros(digits.slice(2));
  }

  return digits.slice(0, MAX_DIGITS);
};

// The value the forms store and send: "+6281211112222", or "" when empty so an
// untouched optional field stays empty rather than saving a bare "+62".
export const formatIndonesianPhone = (input) => {
  const digits = normalizeIndonesianPhoneDigits(input);
  return digits ? `+62${digits}` : "";
};
const COUNTRY_CODE_PREFIX = /^\+(?:62|6|2)?/;

// What a phone field should hold after an edit. The +62 on screen is the
// country code, not part of the number, so only what follows it is read —
// whether the prefix is still intact or the edit has eaten into it. Text that
// does not open with a "+" is a paste replacing the whole field and is
// normalised as a whole, which is what turns a pasted 081211112222 into
// +6281211112222; backspacing past the "+62" empties the field rather than
// leaving a stray 6.
export const applyPhoneEdit = (nextText) => {
  const text = String(nextText ?? "");
  if (!text.startsWith("+")) return formatIndonesianPhone(text);
  return formatIndonesianPhone(
    normalizeIndonesianPhoneDigits(text.replace(COUNTRY_CODE_PREFIX, "")),
  );
};
